// src/store/slices/attendanceSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/attendance/`;

// ==================== ATTENDANCE RECORDS ====================

// ✅ FIXED: Fetch attendance records with proper parameter mapping
export const fetchStudentAttendance = createAsyncThunk(
  'attendance/fetchStudentAttendance',
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      // ✅ Map frontend params to backend API params
      if (params.student_id) queryParams.append('student', params.student_id);
      if (params.class_id) queryParams.append('class_id', params.class_id);
      if (params.date_from) queryParams.append('start_date', params.date_from);
      if (params.date_to) queryParams.append('end_date', params.date_to);
      if (params.status) queryParams.append('status', params.status);
      if (params.active_year_only !== undefined) queryParams.append('active_year_only', params.active_year_only);
      
      const url = queryParams.toString() ? `${BASE_URL}?${queryParams}` : BASE_URL;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch attendance');
    }
  }
);

export const fetchAttendanceRecords = createAsyncThunk(
  'attendance/fetchRecords',
  async (params = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add filters if provided
      if (params.class_id) queryParams.append('class_id', params.class_id);
      if (params.student) queryParams.append('student', params.student);
      if (params.date) queryParams.append('date', params.date);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.status) queryParams.append('status', params.status);
      if (params.active_year_only !== undefined) queryParams.append('active_year_only', params.active_year_only);
      
      const url = queryParams.toString() ? `${BASE_URL}?${queryParams}` : BASE_URL;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Fetch attendance failed');
    }
  }
);

// Add single attendance record
export const addAttendanceRecord = createAsyncThunk(
  'attendance/addRecord',
  async (attendanceData, thunkAPI) => {
    try {
      const response = await axios.post(BASE_URL, attendanceData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Add attendance failed');
    }
  }
);

// Update attendance record
export const updateAttendanceRecord = createAsyncThunk(
  'attendance/updateRecord',
  async ({ id, ...data }, thunkAPI) => {
    try {
      const response = await axios.put(`${BASE_URL}${id}/`, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Update attendance failed');
    }
  }
);

// Delete attendance record
export const deleteAttendanceRecord = createAsyncThunk(
  'attendance/deleteRecord',
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}${id}/`);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Delete attendance failed');
    }
  }
);

// Bulk mark attendance
export const bulkMarkAttendance = createAsyncThunk(
  'attendance/bulkMark',
  async (bulkData, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}bulk-mark/`, bulkData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Bulk mark attendance failed');
    }
  }
);

// ==================== REPORTS & DASHBOARD ====================

// Fetch attendance dashboard data
export const fetchAttendanceDashboard = createAsyncThunk(
  'attendance/fetchDashboard',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`${BASE_URL}dashboard/`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Fetch dashboard failed');
    }
  }
);

// Fetch class attendance report
export const fetchClassAttendanceReport = createAsyncThunk(
  'attendance/fetchClassReport',
  async ({ classId, startDate, endDate }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);
      
      const url = queryParams.toString() 
        ? `${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/attendance/report/?${queryParams}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/attendance/report/`;
        
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Fetch class report failed');
    }
  }
);

// ✅ FIXED: Fetch student attendance summary with correct URL
export const fetchStudentAttendanceSummary = createAsyncThunk(
  'attendance/fetchStudentSummary',
  async (studentId, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/${studentId}/attendance/summary/`
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Fetch student summary failed');
    }
  }
);

// Export attendance report
export const exportAttendanceReport = createAsyncThunk(
  'attendance/exportReport',
  async (params = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.class_id) queryParams.append('class_id', params.class_id);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      
      const url = queryParams.toString() 
        ? `${BASE_URL}export/?${queryParams}`
        : `${BASE_URL}export/`;
        
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `attendance_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      return 'Export completed successfully';
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Export failed');
    }
  }
);

// ==================== SLICE DEFINITION ====================

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState: {
    // Attendance records
    records: [],
    recordsLoading: false,
    recordsError: null,
    
    // Dashboard data
    dashboard: null,
    dashboardLoading: false,
    dashboardError: null,
    
    // Class report
    classReport: null,
    classReportLoading: false,
    classReportError: null,
    
    // Student summary
    studentSummary: null,
    studentSummaryLoading: false,
    studentSummaryError: null,
    
    // Bulk operations
    bulkLoading: false,
    bulkError: null,
    bulkSuccess: null,
    
    // Export
    exportLoading: false,
    exportError: null,
    
    // UI state
    selectedDate: new Date().toISOString().split('T')[0],
    selectedClass: null,
    selectedStudent: null,
    filters: {
      class_id: '',
      status: '',
      start_date: '',
      end_date: '',
      active_year_only: true
    }
  },
  reducers: {
    // UI state management
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSelectedClass: (state, action) => {
      state.selectedClass = action.payload;
    },
    setSelectedStudent: (state, action) => {
      state.selectedStudent = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        class_id: '',
        status: '',
        start_date: '',
        end_date: '',
        active_year_only: true
      };
    },
    
    // Clear states
    clearBulkStatus: (state) => {
      state.bulkError = null;
      state.bulkSuccess = null;
    },
    clearErrors: (state) => {
      state.recordsError = null;
      state.dashboardError = null;
      state.classReportError = null;
      state.studentSummaryError = null;
      state.bulkError = null;
      state.exportError = null;
    },
    
    // Local record management
    updateLocalRecord: (state, action) => {
      const index = state.records.findIndex(record => record.id === action.payload.id);
      if (index !== -1) {
        state.records[index] = { ...state.records[index], ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ==================== FETCH STUDENT ATTENDANCE ====================
      .addCase(fetchStudentAttendance.pending, (state) => {
        state.recordsLoading = true;
        state.recordsError = null;
      })
      .addCase(fetchStudentAttendance.fulfilled, (state, action) => {
        state.recordsLoading = false;
        // ✅ Handle both paginated and non-paginated responses
        state.records = action.payload.results || action.payload;
      })
      .addCase(fetchStudentAttendance.rejected, (state, action) => {
        state.recordsLoading = false;
        state.recordsError = action.payload;
      })
      
      // ==================== FETCH RECORDS ====================
      .addCase(fetchAttendanceRecords.pending, (state) => {
        state.recordsLoading = true;
        state.recordsError = null;
      })
      .addCase(fetchAttendanceRecords.fulfilled, (state, action) => {
        state.recordsLoading = false;
        state.records = action.payload.results || action.payload;
      })
      .addCase(fetchAttendanceRecords.rejected, (state, action) => {
        state.recordsLoading = false;
        state.recordsError = action.payload;
      })
      
      // ==================== ADD RECORD ====================
      .addCase(addAttendanceRecord.pending, (state) => {
        state.recordsLoading = true;
        state.recordsError = null;
      })
      .addCase(addAttendanceRecord.fulfilled, (state, action) => {
        state.recordsLoading = false;
        state.records.unshift(action.payload); // Add to beginning
      })
      .addCase(addAttendanceRecord.rejected, (state, action) => {
        state.recordsLoading = false;
        state.recordsError = action.payload;
      })
      
      // ==================== UPDATE RECORD ====================
      .addCase(updateAttendanceRecord.fulfilled, (state, action) => {
        const index = state.records.findIndex(record => record.id === action.payload.id);
        if (index !== -1) {
          state.records[index] = action.payload;
        }
      })
      .addCase(updateAttendanceRecord.rejected, (state, action) => {
        state.recordsError = action.payload;
      })
      
      // ==================== DELETE RECORD ====================
      .addCase(deleteAttendanceRecord.fulfilled, (state, action) => {
        state.records = state.records.filter(record => record.id !== action.payload);
      })
      .addCase(deleteAttendanceRecord.rejected, (state, action) => {
        state.recordsError = action.payload;
      })
      
      // ==================== BULK MARK ====================
      .addCase(bulkMarkAttendance.pending, (state) => {
        state.bulkLoading = true;
        state.bulkError = null;
        state.bulkSuccess = null;
      })
      .addCase(bulkMarkAttendance.fulfilled, (state, action) => {
        state.bulkLoading = false;
        state.bulkSuccess = action.payload.message || 'Bulk attendance marked successfully';
        
        // Update records with new/updated attendance
        if (action.payload.records) {
          action.payload.records.forEach(newRecord => {
            const existingIndex = state.records.findIndex(r => r.id === newRecord.id);
            if (existingIndex !== -1) {
              state.records[existingIndex] = newRecord;
            } else {
              state.records.unshift(newRecord);
            }
          });
        }
      })
      .addCase(bulkMarkAttendance.rejected, (state, action) => {
        state.bulkLoading = false;
        state.bulkError = action.payload;
      })
      
      // ==================== DASHBOARD ====================
      .addCase(fetchAttendanceDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchAttendanceDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchAttendanceDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })
      
      // ==================== CLASS REPORT ====================
      .addCase(fetchClassAttendanceReport.pending, (state) => {
        state.classReportLoading = true;
        state.classReportError = null;
      })
      .addCase(fetchClassAttendanceReport.fulfilled, (state, action) => {
        state.classReportLoading = false;
        state.classReport = action.payload;
      })
      .addCase(fetchClassAttendanceReport.rejected, (state, action) => {
        state.classReportLoading = false;
        state.classReportError = action.payload;
      })
      
      // ==================== STUDENT SUMMARY ====================
      .addCase(fetchStudentAttendanceSummary.pending, (state) => {
        state.studentSummaryLoading = true;
        state.studentSummaryError = null;
      })
      .addCase(fetchStudentAttendanceSummary.fulfilled, (state, action) => {
        state.studentSummaryLoading = false;
        state.studentSummary = action.payload;
      })
      .addCase(fetchStudentAttendanceSummary.rejected, (state, action) => {
        state.studentSummaryLoading = false;
        state.studentSummaryError = action.payload;
      })
      
      // ==================== EXPORT ====================
      .addCase(exportAttendanceReport.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportAttendanceReport.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportAttendanceReport.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload;
      });
  }
});

// ==================== ACTIONS & SELECTORS ====================

export const {
  setSelectedDate,
  setSelectedClass,
  setSelectedStudent,
  setFilters,
  clearFilters,
  clearBulkStatus,
  clearErrors,
  updateLocalRecord
} = attendanceSlice.actions;

// Selectors
export const selectAttendanceRecords = (state) => state.attendance.records;
export const selectStudentAttendanceRecords = (state) => 
  state.attendance.records.filter(record => 
    record.student === state.attendance.selectedStudentId
  );
export const selectAttendanceLoading = (state) => state.attendance.recordsLoading;
export const selectAttendanceError = (state) => state.attendance.recordsError;
export const selectAttendanceDashboard = (state) => state.attendance.dashboard;
export const selectDashboardLoading = (state) => state.attendance.dashboardLoading;
export const selectClassReport = (state) => state.attendance.classReport;
export const selectStudentSummary = (state) => state.attendance.studentSummary;
export const selectBulkLoading = (state) => state.attendance.bulkLoading;
export const selectBulkSuccess = (state) => state.attendance.bulkSuccess;
export const selectSelectedDate = (state) => state.attendance.selectedDate;
export const selectSelectedClass = (state) => state.attendance.selectedClass;
export const selectAttendanceFilters = (state) => state.attendance.filters;

// Complex selectors
export const selectTodayAttendance = (state) => {
  const today = new Date().toISOString().split('T')[0];
  return state.attendance.records.filter(record => record.date === today);
};

export const selectAttendanceByDate = (state, date) => {
  return state.attendance.records.filter(record => record.date === date);
};

export const selectAttendanceByClass = (state, classId) => {
  return state.attendance.records.filter(record => 
    record.student_class_id === classId || 
    record.class_id === classId
  );
};

export default attendanceSlice.reducer;